import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PlayViewerModal } from '@/components/animation'
import { Loading } from '@/components/ui/Loading'
import './play-animation-page.css'

type PlayMetadata = {
	id: string
	name: string
}

export function PlayAnimationPage() {
	const { playbookId, playId } = useParams<{
		playbookId: string
		playId: string
	}>()
	const navigate = useNavigate()
	const [plays, setPlays] = useState<PlayMetadata[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!playbookId) {
			setError('No playbook ID provided')
			setLoading(false)
			return
		}

		setLoading(true)
		setError(null)

		fetch(`/api/playbooks/${playbookId}`)
			.then((response) => {
				if (!response.ok) {
					throw new Error('Failed to fetch playbook')
				}
				return response.json()
			})
			.then((data) => {
				const sections: unknown[] = Array.isArray(data.sections) ? data.sections : []

				if (sections.length === 0) {
					setError('This playbook has no sections')
					setLoading(false)
					return
				}

				const allPlays: PlayMetadata[] = []
				for (const section of sections) {
					const sectionPlays = (section as Record<string, unknown>).plays
					if (Array.isArray(sectionPlays)) {
						for (const play of sectionPlays) {
							const p = play as Record<string, unknown>
							allPlays.push({
								id: String(p.id),
								name: String(p.name ?? 'Untitled Play'),
							})
						}
					}
				}

				if (allPlays.length === 0) {
					setError('No plays found in this playbook')
				}

				setPlays(allPlays)
				setLoading(false)
			})
			.catch((err) => {
				setError(err instanceof Error ? err.message : 'Unknown error')
				setLoading(false)
			})
	}, [playbookId])

	const handleClose = () => {
		// Navigate back to playbook editor
		navigate(`/playbooks/${playbookId}`)
	}

	if (loading) {
		return <Loading text="Loading animation..." fullscreen />
	}

	if (error || !playbookId || !playId) {
		return (
			<div className="play-animation-page-error">
				<div className="play-animation-page-content">
					<p className="play-animation-page-error-message">Failed to load animation</p>
					<p className="play-animation-page-error-detail">
						{error || 'Missing playbook or play ID'}
					</p>
					<button
						onClick={handleClose}
						className="play-animation-page-button"
					>
						Back to Playbook
					</button>
				</div>
			</div>
		)
	}

	return (
		<PlayViewerModal
			isOpen={true}
			onClose={handleClose}
			playbookId={playbookId}
			initialPlayId={playId}
			plays={plays}
			canEdit={false}
		/>
	)
}
